import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useLocation, connect, useIntl, history, getLocale } from 'umi';
import { NavMenu, SearchBox, Button } from 'tea-component';
import { v4 as uuidv4 } from 'uuid';
import {
  DEFAULT_SEARCH_PARAMS,
  useMountedRef,
  getSearchPageParams,
  updateSearchPageUrl,
  goToSearchPageUrl,
  getHighLine,
} from '@/pages/home/utils/search';
import {
  fetchSearchPullDown,
  deleteHistory,
  fetchAddSearchHistory,
} from '@/pages/home/services/search';
import { CompleteOptionsType, RecentResultType } from '@/pages/home/types/home';
import { debounce } from 'lodash-es';
import SvgIcon from '@home/components/SvgIcon';
import NoData from '@/pages/intelligence/components/NoData';
import useSearchModel from '@home/hooks/useSearchModel';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { dbhAISearchSwitchBtnLogMes } from '@home/utils/buriedPointCfg';
import AISearchSwitch, { getAISearchEnabled } from '@/pages/home/pages/home/components/HeaderContext/AISearchSwitch';
import AISearchList from '@/pages/home/pages/home/components/HeaderContext/AISearchList';
import AIPageNavigationLoading from '@home/components/AINavigation/components/AIPageNavigationLoading';
import AIPageNavigationError from '@home/components/AINavigation/components/AIPageNavigationError';
import useIntelligencePageAIAccess from '@home/components/AINavigation/_hooks/useIntelligencePageAIAccess';
import { logAISearchButtonClick } from '@home/components/AINavigation/utils/log';
import { SEARCH_HISTORY_TYPE } from '@home/_config/constant';
import { DATABRAIN_AGENT_ID2, STORAGE_KEY } from '@/pages/aigc/pages/Agent/config/constant';

export const getTrendingGames = (
  searchTrendingList: any[] = [],
  localeText = '',
  inputValue = '',
) =>
  [...searchTrendingList]
    .filter(item => item.status === 'open')
    .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
    .map((item, index) => {
      const nameCur = localeText === 'zh-CN' ? item.name_cn : item.name_en;

      return {
        groupKey: 'Trending',
        value: item.id,
        id: item.id,
        name: nameCur,
        text: (
          <div key={`${item.id}-${index}`} className="recentResult">
            <span
              dangerouslySetInnerHTML={{
                __html: getHighLine(nameCur, inputValue),
              }}
            ></span>
          </div>
        ),
      };
    });

function Search(props: any) {
  const { dispatch } = props;
  const { formatMessage } = useIntl(); // 中英切换方法
  const localeText = getLocale();
  const location = useLocation();
  const mountedRef = useMountedRef();

  const {
    // AI Page Navigation数据 加载状态
    aiNavigationLoading,
    // AI Page Navigation数据 加载异常
    aiNavigationError,
    // 点击情报页面AI直达按钮的处理方法
    onIntelligencePageAIAccessClick,
    // 点击"回到搜索"按钮
    onIntelligencePageAIAccessBackClick,
    resetIntelligencePageAIAccessError,
  } = useIntelligencePageAIAccess({
    onNavigation: () => {
      setisfocus(false);
    },
    onBackClick: () => {
      // 保持 focus 状态，回退到之前的搜索状态
      setisfocus(true);
    },
  });

  const [recentResult, setRecentResult] = useState<RecentResultType[]>([]);
  const [newResult, setNewResult] = useState([]);
  const [completeOptions, setCompleteOptions] = useState<CompleteOptionsType[]>([]);
  // 下拉搜索值
  const [inputValue, setInputValue] = useState('');
  // 下拉搜索展开与否
  // const [open, setOpen] = useState(false);
  // const [openSure, setOpenSure] = useState(false);
  const [focus, setisfocus] = useState(false);
  // 搜索下拉Trending是否展开
  // const [openTrending, setOpenTrending] = useState(true);
  // AI Search 开关状态
  const [aiSearchEnabled, setAiSearchEnabled] = useState<boolean>(() => getAISearchEnabled());
  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  // 搜索框范围
  const ref = useRef<any>(null);
  // 搜索面板范围（包含 AISearchSwitch 和 SearchBox）
  const refSearchPanel = useRef<any>(null);
  // 下拉框范围
  const reUl = useRef<any>(null);

  // 点击外部区域关闭弹窗,自定义模拟搜索下拉关闭与打开,阻止冒泡事件
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      // 如果不在搜索面板范围内（包含 AISearchSwitch 和 SearchBox）
      if (refSearchPanel.current && !refSearchPanel.current?.contains(event.target)) {
        // 同时不在下拉框范围内
        if (reUl.current && !reUl.current?.contains(event.target)) {
          // 主动关闭下拉
          setisfocus(false);
        }
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleOnFocus = () => {
    if (!history.location.pathname.startsWith('/search')) {
      goToSearchPageUrl({ ...DEFAULT_SEARCH_PARAMS, kw: inputValue });
    }
    setTimeout(() => {
      setisfocus(true);
    }, 100);
  };

  // SameStartDate接口调用hooks
  const {
    openTrending,
    setOpenTrending,
    getOpenTrendingStatus,
    setOpenTrendingStatus,
    searchTrendingList,
    trendingList,
    recentList,
    searchList,
    isShowTrending,
    isSearchPullNoData,
  } = useSearchModel({ completeOptions, inputValue });

  // 输入input后的调用方法
  const onChangeSearchBoxHandle = (value: any) => {
    setInputValue(value);
    getSearchPullDown(value === '' ? ' ' : value);

    if (aiNavigationError && value.trim()) {
      resetIntelligencePageAIAccessError();
    }

    setisfocus(true);
  };

  // 清除input后的调用方法
  const onClearSearchBoxHandle = () => {
    updateSearchPageUrl(DEFAULT_SEARCH_PARAMS, undefined);
    setisfocus(true);
  };

  const onChangeAutoCompleteObjHandle = (strTemp: any) => {
    setInputValue(strTemp?.name);

    updateSearchPageUrl({ ...getSearchPageParams(), kw: strTemp?.name, page: 1 }, undefined);
    addSearchHistoryPullKey(strTemp?.name);
  };

  // onSearch的调用方法
  const onSearchAutoCompleteHandle = (value: any) => {
    const keyword = value?.trim();

    if (aiSearchEnabled && !keyword) {
      return;
    }

    const searchParams = getSearchPageParams();
    const { kw } = searchParams;

    // 重复搜索相同值，不调用
    if (kw !== keyword) {
      updateSearchPageUrl({ ...getSearchPageParams(), kw: keyword, page: 1 }, undefined);
      addSearchHistoryPullKey(keyword);
    }
  };

  const handleSearch = async (kw: String) => {
    const params = {
      keyword: kw,
      entity_type: getSearchPageParams().type,
      system_type: 'pc',
      page: 1,
      page_size: 5,
    };
    try {
      const resultData: any = await fetchSearchPullDown(params);
      setCompleteOptions([]);
      if (mountedRef.current) {
        // 当组件未卸载则set
        if (kw !== ' ') {
          dispatch({
            type: 'searchModels/setSearchTime',
            payload: resultData?.create_time || '',
          });
        }
        setNewResult(resultData?.result || []);
        setRecentResult(resultData?.recent_result || []);
      }
    } catch (err) {
    } finally {
    }
  };

  // 获取首页搜索下拉列表数据,用于搜索时请求的防抖
  const getSearchPullDown = useCallback(
    debounce((kw: string) => {
      handleSearch(kw);
    }, 500),
    [],
  );

  // 删除某个搜索历史记录
  const deleteHistoryHandleSearch = async (id: any, e: any) => {
    e.stopPropagation();
    const params = {
      open_id: userInfo?.openid,
      history_id: Number(id),
    };
    try {
      const result: any = await deleteHistory(params);
      if (result?.code === 0) {
        getSearchPullDown(inputValue != '' ? inputValue : ' ');
      }
    } catch (err) {
    } finally {
    }
  };

  // 添加搜索记录,添加下拉选项记录
  const addSearchHistoryPullKey = (text: String) => {
    fetchAddSearchHistory({
      open_id: userInfo?.openid,
      keyword: text,
      info: {},
      search_type: SEARCH_HISTORY_TYPE.Game,
    });
  };

  const addAISearchHistoryPullKey = (text: String) => {
    fetchAddSearchHistory({
      open_id: userInfo?.openid,
      keyword: text,
      info: {},
      search_type: SEARCH_HISTORY_TYPE.AIAgent,
    });
  };

  const turnCompleteOptions = () => {
    const tempOptions: any = [];
    const recentList: any = [];
    const resultList: any = [];

    recentResult.forEach((item, index) => {
      const searchType = item.search_type || SEARCH_HISTORY_TYPE.Game;

      if (
        !aiSearchEnabled &&
        (searchType === SEARCH_HISTORY_TYPE.AIAgent ||
          searchType === SEARCH_HISTORY_TYPE.AINavigation)
      ) {
        return;
      }

      recentList.push({
        groupKey: 'Recent',
        value: item.id,
        id: item.id,
        name: item.name,
        searchType,
        text: (
          <div key={index} className="recentResult">
            <span dangerouslySetInnerHTML={{ __html: getHighLine(item.name, inputValue) }}></span>
            <Button
              icon="dismiss"
              onClick={(e) => {
                deleteHistoryHandleSearch(item.id, e);
              }}
            />
          </div>
        ),
      });
    });
    recentList?.length > 0 && tempOptions.push(...recentList);

    newResult.forEach((item, index) => {
      resultList.push({
        groupKey: 'Search',
        value: item,
        name: item,
        id: item,
        text: (
          <div className="recentResult" key={index}>
            <span dangerouslySetInnerHTML={{ __html: getHighLine(item, inputValue) }}></span>
          </div>
        ),
      });
    });
    resultList?.length > 0 && tempOptions.push(...resultList);
    setCompleteOptions([
      ...getTrendingGames(searchTrendingList, localeText, inputValue),
      ...tempOptions,
    ]);
  };

  // 任何搜索内容变化，更新下拉列表内容
  useEffect(() => {
    turnCompleteOptions();
  }, [recentResult, newResult, localeText, aiSearchEnabled, searchTrendingList]);

  // 首次进入，先调用一次下拉列表
  useEffect(() => {
    getSearchPullDown(getSearchPageParams().kw !== '' ? getSearchPageParams().kw : ' ');
    getOpenTrendingStatus();
  }, []);

  // 根据url变化，获取path搜索参数
  useEffect(() => {
    if (getSearchPageParams().kw === '') {
      setInputValue('');
    } else {
      setInputValue(getSearchPageParams().kw);
    }
  }, [location.search, location.pathname]);

  const handleTrending = (e: any) => {
    e.stopPropagation();
    setOpenTrending(!openTrending);
    setOpenTrendingStatus(!openTrending);
  };

  const handleTrendingItem = (e: any, item: any) => {
    onChangeAutoCompleteObjHandle(item);
    setisfocus(false);
  };

  const handleRecentItem = (e: any, item: any) => {
    e.stopPropagation();

    if (
      aiSearchEnabled &&
      (item?.searchType === SEARCH_HISTORY_TYPE.AIAgent ||
        item?.searchType === SEARCH_HISTORY_TYPE.AINavigation)
    ) {
      setInputValue(item?.name || '');
      getSearchPullDown(item?.name || ' ');
      setisfocus(true);
      return;
    }

    onChangeAutoCompleteObjHandle(item);
    setisfocus(false);
  };

  // AI Search 开关切换
  const handleAISearchChange = (active: boolean) => {
    setAiSearchEnabled(active);
  };

  // AI Search 列表项点击
  const handleAISearchItemClick = (type: 'agent' | 'navigation') => {
    const sessionId = uuidv4();
    const messageId = uuidv4();

    if (type === 'agent') {
      addAISearchHistoryPullKey(inputValue.trim());

      const logInfo = {
        sessionId,
        mode: 'auto',
        modeId: DATABRAIN_AGENT_ID2,
        from: 'navSearch',
        messageId,
        keywords: inputValue?.trim(),
      };

      logAISearchButtonClick({
        aiSearchType: 'ai_agent',
        data: logInfo,
      });

      // 埋点
      buriedBtnPoint({
        pageId: '700501',
        buttonId: '700501006',
        extInfo: JSON.stringify(logInfo),
        typeId: 'aigc',
      });

      // 设置 Agent 模式映射（将 DATABRAIN_AGENT_ID 的模式设置为 auto）
      try {
        const agentModeMapKey = `__dba_agent_mode_map_${userInfo?.openid}__`;
        const agentModeMap = JSON.parse(localStorage.getItem(agentModeMapKey) || '{}');
        agentModeMap[DATABRAIN_AGENT_ID2] = 'auto';
        localStorage.setItem(agentModeMapKey, JSON.stringify(agentModeMap));
      } catch {
        // ignore
      }

      // 设置选中的 Agent（同步 __dba_selected_agent__ key）
      try {
        const selectedAgentKey = `${STORAGE_KEY}_${userInfo?.openid}__`;
        localStorage.setItem(selectedAgentKey, DATABRAIN_AGENT_ID2);
      } catch {
        // ignore
      }

      history.push({
        pathname: '/agent/chat',
        search: `?sessionId=${sessionId}`,
        state: {
          initialMessage: inputValue.trim(),
          messageId,
          from: 'navSearch',
          serverParams: {
            model_id: DATABRAIN_AGENT_ID2,
            mode: 'auto',
          }
        },
      });
      setisfocus(false);
      return;
    }

    // 情报页面AI直达
    if (type === 'navigation') {
      fetchAddSearchHistory({
        open_id: userInfo?.openid,
        keyword: inputValue.trim(),
        info: {},
        search_type: SEARCH_HISTORY_TYPE.AINavigation,
      });

      const params = {
        keyword: inputValue?.trim() || '',
      };
      void onIntelligencePageAIAccessClick(params);
      return;
    }
  };

  const renderDropdownBoxContent = () => {
    const shouldShowGamesSection = !!searchList?.length || (aiSearchEnabled && !isShowTrending);
    const shouldRenderDropdownList = !isSearchPullNoData || shouldShowGamesSection;

    // 情报页面AI直达加载态
    if (aiNavigationLoading) {
      return (
        <AIPageNavigationLoading
          text={formatMessage({ id: 'dbh_ai_search_page_navigating' })}
        />
      );
    }

    // 情报页面AI直达异常态
    if (aiNavigationError) {
      return (
        <AIPageNavigationError
          errorMessage={formatMessage({ id: 'dbh_ai_search_page_not_found' })}
          backText={formatMessage({ id: 'dbh_ai_search_page_back_to_search' })}
          onBack={onIntelligencePageAIAccessBackClick}
          onClose={() => {
            resetIntelligencePageAIAccessError();
            setisfocus(false);
          }}
        />
      );
    }

    return (
      <>
        {/* AI Search 列表 - 当开关开启且输入框有值时显示 */}
        {aiSearchEnabled && inputValue.trim() && (
          <AISearchList
            inputValue={inputValue}
            onItemClick={handleAISearchItemClick}
          />
        )}
        {shouldRenderDropdownList && (
          <ul className="tea-list tea-list--option tea-list--group" ref={reUl}>
            {isShowTrending && (
              <>
                <li
                  className={`tea-list__label tea-list__label--button ${
                    openTrending ? '' : '--closed'
                  }`}
                  onClick={(e) => {
                    handleTrending(e);
                  }}
                >
                  <span>{formatMessage({ id: 'dbh_search_trending' })}</span>
                  <SvgIcon type="fill-icon-arrow" />
                </li>
                {openTrending &&
                  trendingList?.map((item: any, index: number) => {
                    return (
                      <li
                        key={index}
                        onClick={(e) => {
                          handleTrendingItem(e, item);
                        }}
                      >
                        {item.text}
                      </li>
                    );
                  })}
              </>
            )}
            {!aiSearchEnabled && recentList?.length > 0 && (
              <>
                <li className="tea-list__label">
                  {formatMessage({ id: 'dbh_recent' })}
                </li>
                {recentList?.map((item: any, index: number) => {
                  return (
                    <li
                      key={index}
                      onClick={(e) => {
                        logAISearchButtonClick({
                          aiSearchType: 'game',
                          data: item,
                        });

                        handleRecentItem(e, item);
                      }}
                    >
                      {item.text}
                    </li>
                  );
                })}
              </>
            )}
            {shouldShowGamesSection && (!aiSearchEnabled || (aiSearchEnabled && inputValue?.trim())) && (
              <>
                <li
                  className={
                    aiSearchEnabled
                      ? 'tea-list__label dbh-search-group-title'
                      : 'tea-list__label'
                  }
                >
                  {formatMessage({ id: 'dbh_ai_search_games' })}
                </li>
                {searchList?.map((item: any, index: number) => {
                  return (
                    <li
                      key={index}
                      onClick={(e) => {
                        handleTrendingItem(e, item);
                      }}
                    >
                      {item.text}
                    </li>
                  );
                })}
              </>
            )}
            {aiSearchEnabled && recentList?.length > 0 && (
              <>
                <li className="tea-list__label dbh-search-group-title dbh-search-group-title--with-divider">
                  {formatMessage({ id: 'dbh_recent' })}
                </li>
                {recentList?.map((item: any, index: number) => {
                  return (
                    <li
                      key={index}
                      onClick={(e) => {
                        logAISearchButtonClick({
                          aiSearchType: 'recent',
                          data: item,
                        });

                        handleRecentItem(e, item);
                      }}
                    >
                      {item.text}
                    </li>
                  );
                })}
              </>
            )}
          </ul>
        )}
        {isSearchPullNoData && (
          <NoData text={formatMessage({ id: 'dbi_no_search_results_found' })} />
        )}
      </>
    );
  };

  return (
    <NavMenu.Item>
      <div className="home-header-autoComplete dbh-search-panel" ref={refSearchPanel}>
        <div className={`dbh-search-box-wrapper ${focus ? 'dbh-search-box-wrapper-focus' : ''} ${aiSearchEnabled ? 'dbh-search-box-wrapper--enabled' : ''} ${aiSearchEnabled && focus ? 'dbh-search-box-wrapper--active' : ''} ${!focus && inputValue ? 'dbh-search-box-wrapper--has-value' : ''}`}>
          <div className="dbh-search-box-wrapper__inner">
            <SearchBox
              className={`${focus ? 'lay-nav-search-foces' : 'lay-nav-search'} lay-nav-search-tip`}
              ref={ref}
              onSearch={onSearchAutoCompleteHandle}
              value={inputValue}
              onChange={onChangeSearchBoxHandle}
              onClear={onClearSearchBoxHandle}
              onHelp={() => console.log('help')}
              placeholder={formatMessage({ id: aiSearchEnabled ? 'dbh_ai_search_input_simple_placeholder' : 'dbh_search_placeholder' })}
              onFocus={handleOnFocus}
            />
            <AISearchSwitch
              isActive={aiSearchEnabled}
              onChange={handleAISearchChange}
              buttonLogConfig={dbhAISearchSwitchBtnLogMes.navHeader}
            />
          </div>
        </div>
        <div
          className={`${
            focus ? 'tea-dropdown-box--show' : 'tea-dropdown-box--hide'
          } tea-dropdown-box dbh-search-dropdown-home dbh-search-dropdown-home--header-search`}
        >
          <div className="tea-dropdown-box-inner">{renderDropdownBoxContent()}</div>
        </div>
      </div>
    </NavMenu.Item>
  );
}

export default connect((state) => ({
  searchModels: state.searchModels,
}))(Search);
